import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	type FlyoutMenuItemProps,
	FlyoutMenuItemTrigger,
	type FlyoutMenuItemTriggerProps,
} from '@atlaskit/side-nav-items/flyout-menu-item';
import Spinner from '@atlaskit/spinner';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomFlyoutProps = UnionOmit<FlyoutMenuItemProps, 'isOpen' | 'isDefaultOpen' | 'children'>;
type EntryPointPropsType<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

type Props<TEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<EntryPointPropsType<TEntryPoint>, 'closeFlyout'>;
	errorFallback?: ErrorFallback;
	fallback?: ReactNode;
	isDefaultOpen?: boolean;
	isFlyoutOpen?: boolean;
	onError?: ErrorCallback;
	triggerProps: FlyoutMenuItemTriggerProps & { triggerRef?: React.Ref<HTMLButtonElement> };
} & CustomFlyoutProps;

export type FlyoutCloseHandlerProp = { closeFlyout(): void };

export function FlyoutTrigger<TEntryPoint extends AnyEntryPoint>({
	triggerProps,
	entryPoint,
	entryPointParams,
	entryPointProps,
	errorFallback,
	fallback,
	onError,
	onOpenChange,
	isDefaultOpen = false,
	...flyoutProps
}: Props<TEntryPoint>): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();
	const hasRun = useRef(false);
	const hasLoaded = useRef(false);

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const isOpenControlled = flyoutProps.isFlyoutOpen !== undefined && hasLoaded.current;
	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ReactNode | null>(null);

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (isOpenControlled) {
				// Route through external controller so item.isOpen stays the single source of truth
				onOpenChange?.(open);
			} else {
				setIsOpen(open);
			}
		},
		[isOpenControlled, onOpenChange],
	);

	const handleOpen = useCallback(() => handleOpenChange(true), [handleOpenChange]);
	const handleClose = useCallback(() => {
		if (fg('navx-5052-gsn-flyout-fix')) {
			setIsOpen(false);
			onOpenChange?.(false);
		} else {
			handleOpenChange(false);
		}
	}, [handleOpenChange, onOpenChange]);

	const onFlyoutOpenChange = useCallback(
		(open: boolean) => {
			onOpenChange?.(open);
		},
		[onOpenChange],
	);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// avoid re-loading entry-point when closing flyout
			if (isOpen) {
				return;
			}

			setContent(
				<InternalEntryPointContainer
					id={id}
					entryPointReference={entryPointReference}
					fallback={fallback || <Spinner />}
					errorFallback={errorFallback}
					runtimeProps={
						{
							...(entryPointProps ?? emptyEntryPointProps),
							closeFlyout: handleClose,
						} as unknown as EntryPointPropsType<TEntryPoint>
					}
					onError={onError}
				/>,
			);

			hasLoaded.current = true;
		},
		[entryPointProps, errorFallback, fallback, handleClose, id, isOpen, onError, setContent],
	);

	// Auto-open on first render if isDefaultOpen is true
	useEffect(() => {
		if (!hasRun.current && isDefaultOpen) {
			onLoad({ reference: load() });
			handleOpen();
			hasRun.current = true;
		}
	}, [handleOpen, isDefaultOpen, load, onLoad]);

	// Load entry point immediately if the flyout is open and it hasn't loaded
	useEffect(() => {
		if (flyoutProps.isFlyoutOpen && !hasLoaded.current) {
			onLoad({ reference: load() });
		}
	}, [flyoutProps.isFlyoutOpen, load, onLoad]);

	// Update internal open state when isOpenControlled changes
	// Allows the open state to be dynamically controlled
	useEffect(() => {
		isOpenControlled && setIsOpen(!!flyoutProps.isFlyoutOpen);
	}, [flyoutProps.isFlyoutOpen, isOpenControlled]);

	const handleTriggerClick = useCallback(() => {
		isOpen ? handleClose() : handleOpen();
	}, [handleClose, handleOpen, isOpen]);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({ load, onLoad });

	const flyoutTriggerRef = useMemo(() => {
		const refs: React.Ref<HTMLButtonElement>[] = [triggerRef];
		if (triggerProps?.triggerRef) {
			refs.push(triggerProps.triggerRef);
		}
		return mergeRefs(refs) as React.Ref<HTMLButtonElement>;
	}, [triggerRef, triggerProps.triggerRef]);

	const flyoutContent = useCallback(() => content, [content]);

	return (
		<FlyoutMenuItem
			{...flyoutProps}
			// isDefaultOpen={isDefaultOpen}
			isOpen={isOpen}
			onOpenChange={onFlyoutOpenChange}
		>
			<FlyoutMenuItemTrigger
				{...triggerProps}
				ref={flyoutTriggerRef}
				onClick={handleTriggerClick}
			/>
			<FlyoutMenuItemContent onClose={handleClose}>{flyoutContent()}</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
}
